import { Globe, Check, ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage } from "@/i18n";
import { useTranslation } from "react-i18next";

/**
 * Global language selector rendered as a SINGLE compact control: 🌐 EN ▾
 * Shows the current language CODE (EN / አማ / OR / ትግ / SO) with a globe on the
 * left and a chevron on the right so it reads as one pill, never as separate
 * overlapping elements. `onDark` adapts colors for dark/solid headers.
 */
export const LanguageSelector = ({ onDark = false, className = "" }) => {
  const { t } = useTranslation();
  const { current, setLanguage, languages } = useLanguage();

  const trigger = onDark
    ? "text-white hover:bg-white/15"
    : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/10";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="header-language-toggle"
          aria-label={t("common.changeLanguage")}
          className={`inline-flex h-9 items-center gap-0.5 rounded-lg px-1 outline-none transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 md:h-10 md:gap-1.5 md:px-2.5 ${trigger} ${className}`}
        >
          <Globe className="h-4 w-4 shrink-0 md:h-[18px] md:w-[18px]" />
          <span className="text-[13px] font-semibold leading-none md:text-sm">{current.label}</span>
          <ChevronDown className="hidden h-3 w-3 shrink-0 opacity-70 md:block md:h-3.5 md:w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" data-testid="language-menu">
        <DropdownMenuLabel>{t("common.changeLanguage")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {languages.map((l) => (
          <DropdownMenuItem
            key={l.code}
            data-testid={`language-option-${l.code}`}
            onClick={() => setLanguage(l.code)}
            className="flex items-center justify-between gap-3"
          >
            <span className="flex items-center gap-2.5">
              <span className="inline-flex h-6 w-8 items-center justify-center rounded-md bg-muted text-[11px] font-bold text-foreground">{l.label}</span>
              {l.native}
            </span>
            {current.code === l.code && <Check className="h-4 w-4 text-[#10B981]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
